import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@urc-ui/store/types';
import { ColumnFamily, SelectedFamily } from '@flexible-reports/store/metadata.types';
import { getInitialReportName } from './online-report-slice-utils';
import { REPORT_FORMAT } from '@flexible-reports/utils/report-format-constants';
import { ReportSchedule, ReportDelivery, FlexibleReportPeriod, FlexibleSubscription } from '@flexible-reports/store/slices/types';
import type { FieldValidationError } from '@flexible-reports/store/slices/report-form/validation/types';
import { ServerValidationError } from '@flexible-reports/store/api/server-error-codes';
import { Sort } from '@urc-ui/store/api/types';
import {
  FormModificationState,
  OnlineReportBuilderState,
  ReportFilter,
  ColumnSettings,
  AccountsFilters
} from '@flexible-reports/store/slices/online-report-slices/types';
import { Template } from '@flexible-reports/store/api/templates/types';
import {
  buildSelectedFamiliesForDimensions,
  buildSelectedFamiliesForMetrics
} from '@flexible-reports/pages/shared/column-picker-editor/column-picker-editor-utils';

const initialFormModificationState: FormModificationState = {
  isReportNameModified: false,
  areFiltersModified: false,
  areColumnsModified: false,
  isReportPeriodModified: false,
  isDeliveryModified: false,
  isScheduleModified: false,
  isManagerAccountIdsModified: false,
  isAdvertiserAccountIdsModified: false,
  isGlobalAccountIdsModified: false
};

const initialColumnSettingsState: ColumnSettings = {
  currencyOfVisualization: false,
  availableCurrencyBreakdowns: [],
  selectedCurrencyBreakdowns: null,
  isMtaSelected: false
};

export const initialState: OnlineReportBuilderState = {
  reportConfigurationId: null,
  reportName: getInitialReportName(),
  reportPeriod: {
    preset: 'LAST_7_DAYS'
  },
  accountFilters: {
    managerAccountIds: [],
    advertiserAccountIds: [],
    globalAccountIds: []
  },
  reportFilters: [] as ReportFilter[],
  savedFilterModel: null,
  reportSchedule: {
    frequency: 'ONCE'
  } as ReportSchedule,
  reportDelivery: {
    emails: []
  } as ReportDelivery,
  reportFormat: REPORT_FORMAT.CSV,
  selectedDimensions: [],
  selectedMetrics: [],
  columnSettings: initialColumnSettingsState,
  fieldValidationErrors: [],
  submitErrors: [],
  formModificationState: initialFormModificationState,
  isColumnPickerVisible: false,
  pageSize: 50,
  sortColumn: null
};
const onlineReportSlice = createSlice({
  name: 'onlineReportSlice',
  initialState,
  reducers: {
    updateReportName: (state, action: PayloadAction<{ updatedReportName: string }>) => {
      const { updatedReportName } = action.payload;
      state.reportName = updatedReportName;
      state.formModificationState.isReportNameModified = true;
    },
    setReportPeriod: (state, action: PayloadAction<FlexibleReportPeriod>) => {
      state.reportPeriod = action.payload;
      state.formModificationState.isReportPeriodModified = true;
    },
    setFilters: (state, action: PayloadAction<ReportFilter[]>) => {
      state.reportFilters = action.payload;
      state.formModificationState.areFiltersModified = true;
    },
    setSavedFilterModel: (state, action: PayloadAction<Record<string, unknown> | null>) => {
      state.savedFilterModel = action.payload;
    },
    setAccountsFilter: (state, action: PayloadAction<AccountsFilters>) => {
      state.accountFilters = action.payload;
      state.formModificationState.areFiltersModified = true;
    },
    setManagerAccountsFilter: (state, action: PayloadAction<AccountsFilters['managerAccountIds']>) => {
      state.accountFilters.managerAccountIds = action.payload;
      state.formModificationState.areFiltersModified = true;
      state.formModificationState.isManagerAccountIdsModified = true;
    },
    setGlobalAccountsFilter: (state, action: PayloadAction<AccountsFilters['globalAccountIds']>) => {
      state.accountFilters.globalAccountIds = action.payload;
      state.formModificationState.areFiltersModified = true;
      state.formModificationState.isGlobalAccountIdsModified = true;
    },
    setAdvertisersFilter: (state, action: PayloadAction<AccountsFilters['advertiserAccountIds']>) => {
      state.accountFilters.advertiserAccountIds = action.payload;
      state.formModificationState.areFiltersModified = true;
      state.formModificationState.isAdvertiserAccountIdsModified = true;
    },
    setReportSchedule: (state, action: PayloadAction<{ reportSchedule: ReportSchedule }>) => {
      const { reportSchedule } = action.payload;
      state.reportSchedule = reportSchedule;
      state.formModificationState.isScheduleModified = true;
    },
    setReportDelivery: (state, action: PayloadAction<{ reportDelivery: ReportDelivery }>) => {
      const { reportDelivery } = action.payload;
      state.reportDelivery = reportDelivery;
      state.formModificationState.isDeliveryModified = true;
    },
    setReportName: (state, action: PayloadAction<{ updatedReportName: string }>) => {
      const { updatedReportName } = action.payload;
      state.reportName = updatedReportName;
    },
    setSelectedDimensions: (state, action: PayloadAction<{ selectedDimensions: SelectedFamily[] }>) => {
      const { selectedDimensions } = action.payload;
      state.selectedDimensions = selectedDimensions;
    },
    removeDimensionFamily: (state, action: PayloadAction<{ removedDimensionFamilyId: string }>) => {
      const { removedDimensionFamilyId } = action.payload;
      const familyIndex = state.selectedDimensions.findIndex(dimension => dimension.id === removedDimensionFamilyId);
      if (familyIndex !== -1) {
        state.selectedDimensions.splice(familyIndex, 1);
        state.formModificationState.areColumnsModified = true;
      }
    },
    removeAllDimensions: state => {
      state.selectedDimensions = [];
      state.formModificationState.areColumnsModified = true;
    },
    setSelectedMetrics: (state, action: PayloadAction<{ selectedMetrics: SelectedFamily[] }>) => {
      const { selectedMetrics } = action.payload;
      state.selectedMetrics = selectedMetrics;
    },
    removeMetricFamily: (state, action: PayloadAction<{ removedMetricFamilyId: string }>) => {
      const { removedMetricFamilyId } = action.payload;
      const familyIndex = state.selectedMetrics.findIndex(metric => metric.id === removedMetricFamilyId);
      if (familyIndex !== -1) {
        state.selectedMetrics.splice(familyIndex, 1);
        state.formModificationState.areColumnsModified = true;
      }
    },
    removeAllMetrics: state => {
      state.selectedMetrics = [];
      state.formModificationState.areColumnsModified = true;
    },
    setIsMtaSelected: (state, action: PayloadAction<boolean>) => {
      state.columnSettings.isMtaSelected = action.payload;
      state.formModificationState.areColumnsModified = true;
    },
    setSelectedCurrencyBreakdowns: (state, action: PayloadAction<string | null>) => {
      state.columnSettings.selectedCurrencyBreakdowns = action.payload;
      state.formModificationState.areColumnsModified = true;
    },
    setcurrencyOfVisualization: (state, action: PayloadAction<boolean>) => {
      state.columnSettings.currencyOfVisualization = action.payload;
      state.formModificationState.areColumnsModified = true;
    },
    resetCreateReportForm: state => {
      return {
        ...initialState,
        accountFilters: {
          ...state.accountFilters
        },
        reportName: getInitialReportName()
      };
    },
    clearErrors: state => {
      state.fieldValidationErrors = [];
      state.submitErrors = [];
    },
    setFieldValidationErrors: (state, action: PayloadAction<FieldValidationError[]>) => {
      state.fieldValidationErrors = action.payload;
    },
    setSubmitErrors: (state, action: PayloadAction<ServerValidationError[]>) => {
      state.submitErrors = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setSortColumn: (state, action: PayloadAction<Sort | null>) => {
      state.sortColumn = action.payload;
    },
    toggleColumnPickerVisibility: state => {
      state.isColumnPickerVisible = !state.isColumnPickerVisible;
    },
    hydrateFromFlexSubscription: (
      state,
      action: PayloadAction<{
        subscription: FlexibleSubscription;
        isEditMode: boolean;
      }>
    ) => {
      const { subscription, isEditMode } = action.payload;
      state.reportName = subscription.reportName;
      state.reportPeriod = subscription.reportPeriod;
      state.reportSchedule = subscription.reportSchedule;
      state.reportDelivery = subscription.reportDelivery;
      state.accountFilters.managerAccountIds = subscription.linkedAccounts
        .filter(acc => acc.managerAccountId)
        .map(acc => acc.managerAccountId as string);
      state.accountFilters.advertiserAccountIds = subscription.linkedAccounts
        .filter(acc => acc.advertiserAccountId)
        .map(acc => acc.advertiserAccountId as string);

      // TODO: https://sim.amazon.com/issues/RCMRX-12575 Add global accounts, selected columns, column settings, filters and sort config

      if (isEditMode) {
        state.reportConfigurationId = subscription.reportConfigurationId;
      }
    },
    hydrateFromTemplate: (
      state,
      action: PayloadAction<{
        template: Template;
        dimFamilies: ColumnFamily[];
        metricFamilies: ColumnFamily[];
      }>
    ) => {
      const { template, dimFamilies, metricFamilies } = action.payload;

      if (template) {
        state.reportName = getInitialReportName(template.name);
        if (template.query && dimFamilies.length > 0 && metricFamilies.length > 0) {
          const selectedDimensionFamilies = buildSelectedFamiliesForDimensions(dimFamilies, template.query.fields);
          const selectedMetricFamilies = buildSelectedFamiliesForMetrics(metricFamilies, template.query.fields);
          state.selectedDimensions = selectedDimensionFamilies;
          state.selectedMetrics = selectedMetricFamilies;
        }
      }
    }
  }
});

export const {
  updateReportName,
  setFilters,
  setSavedFilterModel,
  setAccountsFilter,
  setManagerAccountsFilter,
  setGlobalAccountsFilter,
  setAdvertisersFilter,
  setReportSchedule,
  setReportDelivery,
  setReportName,
  setSelectedDimensions,
  removeDimensionFamily,
  removeAllDimensions,
  setSelectedMetrics,
  removeMetricFamily,
  removeAllMetrics,
  setIsMtaSelected,
  setSelectedCurrencyBreakdowns,
  setcurrencyOfVisualization,
  resetCreateReportForm,
  setReportPeriod,
  clearErrors,
  setFieldValidationErrors,
  setSubmitErrors,
  setPageSize,
  setSortColumn,
  toggleColumnPickerVisibility,
  hydrateFromFlexSubscription,
  hydrateFromTemplate
} = onlineReportSlice.actions;

export const selectReportConfigurationId = (state: RootState) => state.OnlineReportBuilder.reportConfigurationId;
export const selectReportName = (state: RootState) => state.OnlineReportBuilder.reportName;
export const selectReportPeriod = (state: RootState) => state.OnlineReportBuilder.reportPeriod;
export const selectAccountFilters = (state: RootState) => state.OnlineReportBuilder.accountFilters;
export const selectManagerAccountIds = (state: RootState) => state.OnlineReportBuilder.accountFilters.managerAccountIds;
export const selectAdvertiserAccountIds = (state: RootState) => state.OnlineReportBuilder.accountFilters.advertiserAccountIds;
export const selectGlobalAccountIds = (state: RootState) => state.OnlineReportBuilder.accountFilters.globalAccountIds;
export const selectReportFilters = (state: RootState) => state.OnlineReportBuilder.reportFilters;
export const selectSavedFilterModel = (state: RootState) => state.OnlineReportBuilder.savedFilterModel;
export const selectReportSchedule = (state: RootState) => state.OnlineReportBuilder.reportSchedule;
export const selectReportDelivery = (state: RootState) => state.OnlineReportBuilder.reportDelivery;
export const selectReportFormat = (state: RootState) => state.OnlineReportBuilder.reportFormat;
export const selectSelectedDimensions = (state: RootState) => state.OnlineReportBuilder.selectedDimensions;
export const selectSelectedMetrics = (state: RootState) => state.OnlineReportBuilder.selectedMetrics;
export const selectColumnSettings = (state: RootState) => state.OnlineReportBuilder.columnSettings;
export const selectCurrencyOfVisualization = (state: RootState) => state.OnlineReportBuilder.columnSettings.currencyOfVisualization;
export const selectAvailableCurrencyBreakdowns = (state: RootState) => state.OnlineReportBuilder.columnSettings.availableCurrencyBreakdowns;
export const selectselectedCurrencyBreakdowns = (state: RootState) => state.OnlineReportBuilder.columnSettings.selectedCurrencyBreakdowns;
export const selectisMtaSelected = (state: RootState) => state.OnlineReportBuilder.columnSettings.isMtaSelected;
export const selectFieldValidationErrors = (state: RootState) => state.OnlineReportBuilder.fieldValidationErrors;
export const selectSubmitErrors = (state: RootState) => state.OnlineReportBuilder.submitErrors;
export const selectFormModificationState = (state: RootState) => state.OnlineReportBuilder.formModificationState;
export const selectIsColumnPickerVisible = (state: RootState) => state.OnlineReportBuilder.isColumnPickerVisible;
export const selectPageSize = (state: RootState) => state.OnlineReportBuilder.pageSize;
export const selectSortColumn = (state: RootState) => state.OnlineReportBuilder.sortColumn;
export const getCreateReportFormData = (state: RootState) => state.OnlineReportBuilder;

export default onlineReportSlice.reducer;
